// The Calendar tab: Day, Week and Month place tasks with the Plan tab's rules
// (itemsOnDay). A task sits in an hour grid only when it keeps a time on the
// day it sits on; everything else is in that day's all-day row. Google events
// join them in the views.

import type { DateStr, Task } from '@/types'
import { addDays, addMonthsClamped, dateTimeFrom, endOfMonth, endOfWeek, startOfMonth, startOfWeek } from './dates'
import { itemsOnDay, type PlanItem } from './plan'

export interface MonthMark {
  kind: 'task' | 'event' | 'faint'
  color: string | null
}

export interface MonthCell {
  date: DateStr | null
  day: number | null
  marks: MonthMark[]
  count: number
}

/** The phone Month: a Monday-first grid of up to six weeks, with up to three marks per day. */
export function monthCells(tasks: Task[], monthStart: DateStr, today: DateStr, colorOf: (t: Task) => string | null, extraMarks: (d: DateStr) => MonthMark[] = () => []): MonthCell[] {
  const first = monthStart.slice(0, 8) + '01'
  const { y, m } = { y: Number(first.slice(0, 4)), m: Number(first.slice(5, 7)) }
  const daysInMonth = new Date(Date.UTC(y, m, 0)).getUTCDate()
  const offset = (new Date(Date.UTC(y, m - 1, 1)).getUTCDay() + 6) % 7
  const total = Math.ceil((offset + daysInMonth) / 7) * 7
  const cells: MonthCell[] = []
  for (let k = 0; k < total; k += 1) {
    const n = k - offset + 1
    if (n < 1 || n > daysInMonth) {
      cells.push({ date: null, day: null, marks: [], count: 0 })
      continue
    }
    const date = `${first.slice(0, 8)}${String(n).padStart(2, '0')}`
    const items = itemsOnDay(tasks, date, today, false, true).filter((i) => !i.done)
    const marks: MonthMark[] = items.map((it): MonthMark => ({ kind: it.faint ? 'faint' : 'task', color: colorOf(it.task) })).concat(extraMarks(date))
    cells.push({ date, day: n, marks: marks.slice(0, 3), count: marks.length })
  }
  return cells
}

/** A FullCalendar event input for a task, as the Week and wide Month grids draw it. */
export interface GridEntry {
  id: string
  title: string
  start: Date
  end: Date
  allDay: boolean
  editable: boolean
  extendedProps: {
    kind: 'task'
    taskId: string
    item: PlanItem
    color: string | null
    isPrivate: boolean
  }
}

/**
 * Tasks within a range as grid entries. A task keeping a time on its day takes
 * a one-hour slot from it; any other open task, a preview or a done row sits in
 * the all-day row, unless `timedOnly`, as the phone Week asks, since it lists a
 * day's tasks under the grid. Only real open cards can be dragged.
 */
export function gridEntries(tasks: Task[], from: DateStr, to: DateStr, today: DateStr, colorOf: (t: Task) => string | null, hideDone = false, timedOnly = false): GridEntry[] {
  const out: GridEntry[] = []
  for (let d = from; d <= to; d = addDays(d, 1)) {
    for (const item of itemsOnDay(tasks, d, today, hideDone, true)) {
      const props = { kind: 'task' as const, taskId: item.task.id, item, color: colorOf(item.task), isPrivate: item.task.scope === 'private' }
      const editable = !item.done && !item.faint && !item.dueMark
      const id = `${item.key}#${d}`
      if (item.time && !item.done && !item.faint) {
        const start = dateTimeFrom(d, item.time)
        out.push({ id, title: item.task.title, start, end: new Date(start.getTime() + 3600_000), allDay: false, editable, extendedProps: props })
      } else if (!timedOnly) {
        out.push({ id, title: item.task.title, start: dateTimeFrom(d, '00:00'), end: dateTimeFrom(addDays(d, 1), '00:00'), allDay: true, editable, extendedProps: props })
      }
    }
  }
  return out
}

export type CalendarPeriod = 'day' | 'week' | 'month'

/**
 * The day selected after an arrow or a swipe: the next or previous day, the
 * same weekday a week on, or the first of the next month, which is today when
 * that month is this one.
 */
export function stepDate(period: CalendarPeriod, date: DateStr, dir: 1 | -1, today: DateStr): DateStr {
  if (period === 'day') return addDays(date, dir)
  if (period === 'week') return addDays(date, 7 * dir)
  const first = addMonthsClamped(startOfMonth(date), dir)
  return first.slice(0, 7) === today.slice(0, 7) ? today : first
}

/** The days a period loads events for: Day its week, Week that week and one either side, Month its grid and a week either side. */
export function periodWindow(period: CalendarPeriod, date: DateStr): { from: DateStr; to: DateStr } {
  if (period === 'day') return { from: startOfWeek(date), to: endOfWeek(date) }
  if (period === 'week') return { from: addDays(startOfWeek(date), -7), to: addDays(endOfWeek(date), 7) }
  return { from: addDays(startOfWeek(startOfMonth(date)), -7), to: addDays(endOfWeek(endOfMonth(date)), 7) }
}
